import React, { useState, useEffect, useCallback } from 'react';
import { canManageFinance } from '@/lib/permissions';
import { api } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Plus, Trash2, ArrowUpCircle, ArrowDownCircle, Send, Pencil, Save, X, CheckCircle2, GripVertical, ArrowUpDown } from 'lucide-react';
import { toast } from 'sonner';
import { SearchableSelect } from '@/components/SearchableSelect';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const Investments = ({ user }) => {
  const [investments, setInvestments] = useState([]);
  const [managers, setManagers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [periodClosed, setPeriodClosed] = useState(false);
  
  const [currentPeriodMonths, setCurrentPeriodMonths] = useState(1);
  const [currentPeriodMonthsList, setCurrentPeriodMonthsList] = useState([]);

  // History dialog
  const [historyDialog, setHistoryDialog] = useState({ open: false, loading: false, data: null });
  const [historyViewMode, setHistoryViewMode] = useState('last10');
  const [historyStartDate, setHistoryStartDate] = useState('');
  const [historyEndDate, setHistoryEndDate] = useState('');
  
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    investmentId: null,
    investmentAmount: 0,
    managerName: '',
    branchName: '',
    password: ''
  });
  
  // Distribution status
  const [distributionStatus, setDistributionStatus] = useState({ has_investments: false, is_distributed: false });
  const [distributing, setDistributing] = useState(false);
  const [distributedMap, setDistributedMap] = useState({}); // { manager_id: total Hasil Invest for period }
  const [pembagianDialog, setPembagianDialog] = useState(false);
  const [pembagianRows, setPembagianRows] = useState({}); // { manager_id: [{pct, hasil}, ...] }
  const [bulkPembagianPct, setBulkPembagianPct] = useState('');
  const [submittingPembagian, setSubmittingPembagian] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [centralAccounts, setCentralAccounts] = useState([]);
  const [branchAccounts, setBranchAccounts] = useState([]);
  const [otherAccounts, setOtherAccounts] = useState([]);
  const [customerAccounts, setCustomerAccounts] = useState([]);
  
  const [formData, setFormData] = useState({
    manager_id: '',
    branch_ids: [],
    investment_amount: '',
    investment_date: new Date().toISOString().split('T')[0],
    period_id: '',
    account_type: '',
    account_id: ''
  });
  
  const [transferForm, setTransferForm] = useState({
    investor_id: '',
    manager_name: '',
    account_type: '',
    account_id: '',
    amount: '',
    description: '',
    transfer_type: 'DEPOSIT',
    transfer_date: new Date().toISOString().split('T')[0]
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const periodId = localStorage.getItem('selectedPeriodId');
      const params = periodId ? { period_id: periodId } : {};
      
      const [investmentsRes, managersRes, branchesRes, centralRes, branchesFullRes, otherRes, customerRes, periodsRes, distStatusRes] = await Promise.all([
        api.get('/investments', { params }),
        api.get('/managers'),
        api.get('/branches'),
        api.get('/central-accounts'),
        api.get('/branches'),
        api.get('/other-accounts'),
        api.get('/customer-accounts'),
        api.get('/periods'),
        api.get('/investments/distribution-status', { params })
      ]);
      
      setInvestments(investmentsRes.data);
      setManagers(managersRes.data);
      setBranches(branchesRes.data);
      setCentralAccounts(centralRes.data);
      setOtherAccounts(otherRes.data);
      setCustomerAccounts(customerRes.data);
      setDistributionStatus(distStatusRes.data);

      // Distributed Hasil Invest per manager for the selected period (INVESTASI_MITRA expenses)
      try {
        const ceRes = await api.get('/central-expenses', { params });
        const dmap = {};
        (ceRes.data || []).forEach(ce => {
          if (ce.expense_type === 'INVESTASI_MITRA' && ce.manager_id) {
            dmap[ce.manager_id] = (dmap[ce.manager_id] || 0) + (ce.amount || 0);
          }
        });
        setDistributedMap(dmap);
      } catch (e) { setDistributedMap({}); }
      
      // Check if current period is closed
      if (periodId) {
        const currentPeriod = periodsRes.data.find(p => p.id === periodId);
        setPeriodClosed(currentPeriod?.is_closed || false);
        const monthsArr = Array.isArray(currentPeriod?.months) ? currentPeriod.months : [];
        setCurrentPeriodMonthsList(monthsArr);
        setCurrentPeriodMonths(monthsArr.length > 0 ? monthsArr.length : 1);
      }
      
      const allBranchAccounts = [];
      branchesFullRes.data.forEach(branch => {
        if (branch.accounts) {
          branch.accounts.forEach(acc => {
            allBranchAccounts.push({ ...acc, branch_name: branch.name });
          });
        }
      });
      setBranchAccounts(allBranchAccounts);
      
      if (periodId) {
        setFormData(prev => ({ ...prev, period_id: periodId }));
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Gagal memuat data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') fetchData();
    };
    const handleFocus = () => fetchData();
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [fetchData]);

  // Group investments by manager (ordered by manager order_number for drag-drop persistence)
  const managerInvestments = React.useMemo(() => {
    const orderMap = {};
    managers.forEach(m => { orderMap[m.id] = (m.order_number ?? 9999); });
    const grouped = {};
    investments.forEach(inv => {
      if (!grouped[inv.manager_id]) {
        grouped[inv.manager_id] = {
          manager_id: inv.manager_id,
          manager_name: inv.manager_name,
          shares_percent: inv.shares_percent || 0,
          investment_amount: inv.investment_amount || 0,
          manager_balance: inv.manager_balance || 0,
          dividend_months: inv.dividend_months || 1,
          order_number: orderMap[inv.manager_id] ?? 9999,
          investments: []
        };
      }
      grouped[inv.manager_id].investments.push(inv);
    });
    return Object.values(grouped).sort((a, b) =>
      (a.order_number - b.order_number) || String(a.manager_name).localeCompare(String(b.manager_name))
    );
  }, [investments, managers]);

  const [reorderMode, setReorderMode] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const SortableInvRow = ({ id, children }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
    return children({ setNodeRef, style, attributes, listeners });
  };

  const handleInvestasiDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldI = managerInvestments.findIndex(m => m.manager_id === active.id);
    const newI = managerInvestments.findIndex(m => m.manager_id === over.id);
    if (oldI === -1 || newI === -1) return;
    const reordered = arrayMove(managerInvestments, oldI, newI);
    const items = reordered.map((m, idx) => ({ id: m.manager_id, order_number: idx + 1 }));
    // Optimistic update of local managers order
    setManagers(prev => prev.map(m => {
      const upd = items.find(i => i.id === m.id);
      return upd ? { ...m, order_number: upd.order_number } : m;
    }));
    try {
      await api.put('/managers/reorder', { items });
      toast.success('Urutan berhasil diperbarui');
      fetchData();
    } catch (e) {
      toast.error('Gagal memperbarui urutan');
      fetchData();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.manager_id) {
      toast.error('Pilih mitra');
      return;
    }
    const periodId = localStorage.getItem('selectedPeriodId');
    if (!periodId) {
      toast.error('Pilih periode terlebih dahulu');
      return;
    }
    if (submitting) return;
    setSubmitting(true);
    try {
      const response = await api.post('/investments/register', {
        manager_id: formData.manager_id,
        period_id: periodId,
      });
      toast.success(response.data.message || 'Mitra berhasil ditambahkan ke investasi');
      setDialogOpen(false);
      fetchData();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menambahkan investasi');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (id) => {
    const investment = investments.find(inv => inv.id === id);
    if (!investment) { toast.error('Investasi tidak ditemukan'); return; }
    setDeleteDialog({
      open: true,
      investmentId: investment.id,
      investmentAmount: investment.investment_amount || 0,
      managerName: investment.manager_name,
      branchName: investment.branch_name,
      password: ''
    });
  };

  const handleConfirmDelete = async (e) => {
    e.preventDefault();
    if (!deleteDialog.password?.trim()) {
      toast.error('Password harus diisi untuk menghapus investasi');
      return;
    }
    if (submitting) return;
    setSubmitting(true);
    try {
      await api.delete(`/investments/${deleteDialog.investmentId}`, {
        data: { password: deleteDialog.password }
      });
      toast.success('Investasi berhasil dihapus');
      setDeleteDialog({ open: false, investmentId: null, investmentAmount: 0, managerName: '', branchName: '', password: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menghapus investasi');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    const periodId = localStorage.getItem('selectedPeriodId');
    setFormData({
      manager_id: '', branch_ids: [], investment_amount: '',
      investment_date: new Date().toISOString().split('T')[0],
      period_id: periodId || '', account_type: '', account_id: ''
    });
  };

  const resetTransferForm = () => {
    setTransferForm({
      investor_id: '', manager_name: '', account_type: '', account_id: '',
      amount: '', description: '', transfer_type: 'DEPOSIT',
      transfer_date: new Date().toISOString().split('T')[0]
    });
  };

  const openTransferDialog = (managerData) => {
    setTransferForm({
      ...transferForm,
      investor_id: managerData.manager_id,
      manager_name: managerData.manager_name
    });
    setTransferDialogOpen(true);
  };

  const getAccountsByType = (type) => {
    switch (type) {
      case 'CENTRAL': return centralAccounts;
      case 'BRANCH': return branchAccounts;
      case 'HUTANG_USAHA': return otherAccounts;
      case 'CUSTOMER': return customerAccounts;
      case 'MANAGER':
        return managers.flatMap(m => (m.accounts || []));
      default: return [];
    }
  };

  const handleTransferSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await api.post('/managers/transfer', {
        ...transferForm,
        amount: parseFloat(transferForm.amount)
      });
      toast.success(`${transferForm.transfer_type === 'DEPOSIT' ? 'Deposit' : 'Withdraw'} berhasil`);
      setTransferDialogOpen(false);
      resetTransferForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal melakukan transfer');
    } finally {
      setSubmitting(false);
    }
  };

  const openPembagian = async () => {
    const periodId = localStorage.getItem('selectedPeriodId');
    let detail = {};
    try {
      const res = await api.get('/investments/distribution-detail', { params: { period_id: periodId } });
      detail = res.data || {};
    } catch (e) { detail = {}; }
    const months = currentPeriodMonthsList.length
      ? currentPeriodMonthsList
      : Array.from({ length: currentPeriodMonths }, (_, i) => String(i));
    const rows = {};
    managerInvestments.forEach(m => {
      const mDetail = detail[m.manager_id] || {};
      rows[m.manager_id] = months.map((ym) => {
        const d = mDetail[ym];
        if (!d) return { pct: '', hasil: '', savedPct: null, savedAmount: null };
        if (d.percent !== null && d.percent !== undefined) {
          return { pct: String(d.percent), hasil: '', savedPct: d.percent, savedAmount: d.amount };
        }
        const amt = (d.amount !== null && d.amount !== undefined) ? String(d.amount) : '';
        return { pct: '', hasil: amt, savedPct: null, savedAmount: d.amount };
      });
    });
    setPembagianRows(rows);
    setBulkPembagianPct('');
    setPembagianDialog(true);
  };

  const submitPembagian = async () => {
    const periodId = localStorage.getItem('selectedPeriodId');
    if (!periodId) { toast.error('Pilih periode terlebih dahulu'); return; }
    const distributions = managerInvestments.map(m => {
      const arr = pembagianRows[m.manager_id] || [];
      return {
        manager_id: m.manager_id,
        percents: arr.map(r => (r && r.pct !== '' ? (parseFloat(r.pct) || 0) : 0)),
        amounts: arr.map(r => (r && r.pct === '' ? (parseFloat(r.hasil) || 0) : 0)),
      };
    });
    setSubmittingPembagian(true);
    try {
      const response = await api.post('/investments/distribute-manual', {
        period_id: periodId,
        distributions,
      });
      toast.success(response.data.message || 'Pembagian investasi berhasil');
      setPembagianDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal memproses Pembagian Investasi');
    } finally {
      setSubmittingPembagian(false);
    }
  };

  const [editingDesc, setEditingDesc] = useState({ id: null, value: '', saving: false });

  const openHistoryDialog = async (managerId, managerName) => {
    setHistoryViewMode('last10');
    setHistoryStartDate('');
    setHistoryEndDate('');
    setEditingDesc({ id: null, value: '', saving: false });
    setHistoryDialog({ open: true, loading: true, data: null, managerId, managerName });
    try {
      const res = await api.get(`/investments/manager/${managerId}/history`);
      setHistoryDialog({ open: true, loading: false, data: res.data, managerId, managerName });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal memuat riwayat investasi');
      setHistoryDialog({ open: false, loading: false, data: null });
    }
  };

  const saveHistoryDescription = async (row) => {
    const value = editingDesc.value.trim();
    if (!row.refId || value === (row.description || '')) {
      setEditingDesc({ id: null, value: '', saving: false });
      return;
    }
    setEditingDesc((prev) => ({ ...prev, saving: true }));
    try {
      const url = row.kind === 'profit' ? `/central-expenses/${row.refId}` : `/account-transactions/${row.refId}`;
      await api.put(url, { description: value });
      const res = await api.get(`/investments/manager/${historyDialog.managerId}/history`);
      setHistoryDialog((prev) => ({ ...prev, data: res.data }));
      toast.success('Keterangan diperbarui');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal memperbarui keterangan');
    } finally {
      setEditingDesc({ id: null, value: '', saving: false });
    }
  };

  const monthLabel = (ym) => {
    const names = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];
    if (!ym || typeof ym !== 'string' || !ym.includes('-')) return ym || '';
    const [y, m] = ym.split('-');
    const idx = parseInt(m, 10) - 1;
    return (idx >= 0 && idx < 12) ? `${names[idx]} ${y}` : ym;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency', currency: 'IDR', minimumFractionDigits: 0
    }).format(amount).replace('Rp', 'Rp.');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  const totalDanaInvestasi = managerInvestments.reduce((sum, m) => sum + m.investment_amount, 0);
  const totalHasilInvest = managerInvestments.reduce((sum, m) => sum + (distributedMap[m.manager_id] || 0), 0);
  const rowHasil = (row, base) => {
    if (!row) return 0;
    const pctActive = row.pct !== '' && row.pct !== null && row.pct !== undefined;
    if (pctActive) {
      // Bulan yang sudah tersimpan & persennya belum diubah: pakai nominal tersimpan
      if (row.savedAmount !== null && row.savedAmount !== undefined && row.savedPct !== null
          && row.savedPct !== undefined && parseFloat(row.pct) === parseFloat(row.savedPct)) {
        return Math.round(row.savedAmount);
      }
      return Math.round((parseFloat(row.pct) || 0) / 100 * base);
    }
    return Math.round(parseFloat(row.hasil) || 0);
  };
  const pembagianTotalHasil = managerInvestments.reduce((sum, m) => {
    const base = (m.investment_amount || 0) - (distributedMap[m.manager_id] || 0);
    const rows = pembagianRows[m.manager_id] || [];
    return sum + rows.reduce((s, r) => s + rowHasil(r, base), 0);
  }, 0);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-800" data-testid="investments-title">Investasi Mitra</h1>
          <p className="text-slate-600 mt-1">Kelola investasi dan saham mitra</p>
        </div>
        <div className="flex gap-2">
          {canManageFinance(user) && !periodClosed && (
            <>
              {managerInvestments.length > 0 && (
                <Button
                  variant={distributionStatus.is_distributed ? "outline" : "default"}
                  onClick={openPembagian}
                  className={distributionStatus.is_distributed 
                    ? "border-green-500 text-green-700 hover:bg-green-50" 
                    : "bg-purple-600 hover:bg-purple-700"}
                  data-testid="distribute-btn"
                >
                  {distributionStatus.is_distributed ? (
                    <CheckCircle2 size={16} className="mr-2" />
                  ) : (
                    <ArrowUpCircle size={16} className="mr-2" />
                  )}
                  {distributionStatus.is_distributed ? 'Pembagian Investasi (Sudah Dibagikan)' : 'Pembagian Investasi'}
                </Button>
              )}
              <Button onClick={() => setDialogOpen(true)} className="bg-teal-600 hover:bg-teal-700" data-testid="add-investment-btn">
                <Plus size={20} className="mr-2" />
                Tambah Investasi
              </Button>
            </>
          )}
        </div>
      </div>


      {periodClosed && (
        <Card className="border-l-4 border-l-orange-500 bg-orange-50">
          <CardContent className="pt-4 pb-4">
            <p className="text-sm text-orange-800 font-medium">
              Periode ini sudah ditutup. Saham tidak dapat diubah.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Investments Table */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Daftar Investasi</CardTitle>
            {canManageFinance(user) && (
              <Button
                variant={reorderMode ? "default" : "outline"}
                size="sm"
                onClick={() => setReorderMode(!reorderMode)}
                className={`gap-2 ${reorderMode ? "bg-amber-600 hover:bg-amber-700" : ""}`}
                data-testid="investasi-reorder-toggle"
              >
                <ArrowUpDown size={16} /> {reorderMode ? 'Selesai Susun' : 'Susun Urutan'}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {managerInvestments.length === 0 ? (
            <div className="text-center py-12 text-slate-500">Belum ada data investasi</div>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleInvestasiDragEnd}>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">No.</TableHead>
                    <TableHead>Mitra</TableHead>
                    <TableHead className="text-right">Saldo Investasi</TableHead>
                    <TableHead className="text-right">Hasil Invest Periode Ini</TableHead>
                    {canManageFinance(user) && <TableHead className="text-center">Transfer</TableHead>}
                    {canManageFinance(user) && <TableHead className="text-center">Hapus</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <SortableContext items={managerInvestments.map(m => m.manager_id)} strategy={verticalListSortingStrategy}>
                  {managerInvestments.map((mgrData, index) => {
                    const hasilInvest = distributedMap[mgrData.manager_id] || 0;
                    return (
                      <SortableInvRow key={mgrData.manager_id} id={mgrData.manager_id}>
                        {({ setNodeRef, style, attributes, listeners }) => (
                      <TableRow ref={setNodeRef} style={style} data-testid={`investment-row-${index}`}>
                        <TableCell className="text-center">
                          {reorderMode ? (
                            <span {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing inline-flex" data-testid="drag-handle-investasi"><GripVertical size={16} className="text-slate-400" /></span>
                          ) : (index + 1)}
                        </TableCell>
                        <TableCell className="font-medium">{mgrData.manager_name}</TableCell>

                        <TableCell className="text-right font-medium text-teal-700">
                          <button
                            type="button"
                            onClick={() => openHistoryDialog(mgrData.manager_id, mgrData.manager_name)}
                            className="hover:underline hover:text-teal-800 focus:outline-none"
                            data-testid={`saldo-investasi-btn-${mgrData.manager_id}`}
                            title="Lihat riwayat transaksi investasi"
                          >
                            {formatCurrency(mgrData.investment_amount)}
                          </button>
                        </TableCell>

                        <TableCell className="text-right font-semibold text-purple-700" data-testid={`hasil-invest-${mgrData.manager_id}`}>
                          {formatCurrency(hasilInvest)}
                        </TableCell>

                        {canManageFinance(user) && (
                          <TableCell className="text-center">
                            <Button variant="ghost" size="sm" onClick={() => openTransferDialog(mgrData)}
                              className="text-teal-600 hover:text-teal-700 hover:bg-teal-50"
                              data-testid={`transfer-btn-${mgrData.manager_id}`}>
                              <Send size={16} />
                            </Button>
                          </TableCell>
                        )}

                        {canManageFinance(user) && (
                          <TableCell className="text-center">
                            {mgrData.investments.map(inv => (
                              <Button key={inv.id} variant="ghost" size="sm" onClick={() => handleDelete(inv.id)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                data-testid={`delete-btn-${mgrData.manager_id}`}
                                title="Hapus investasi">
                                <Trash2 size={16} />
                              </Button>
                            ))}
                          </TableCell>
                        )}
                      </TableRow>
                        )}
                      </SortableInvRow>
                    );
                  })}
                  </SortableContext>
                </TableBody>
                {managerInvestments.length > 0 && (
                  <TableFooter>
                    <TableRow className="bg-teal-50 border-t-2 border-teal-600">
                      <TableCell colSpan={2} className="text-right font-bold text-teal-900">TOTAL</TableCell>
                      <TableCell className="text-right font-bold text-teal-900 text-lg">
                        {formatCurrency(totalDanaInvestasi)}
                      </TableCell>
                      <TableCell className="text-right font-bold text-purple-900 text-lg">
                        {formatCurrency(totalHasilInvest)}
                      </TableCell>
                      {canManageFinance(user) && <TableCell colSpan={2}></TableCell>}
                    </TableRow>
                  </TableFooter>
                )}
              </Table>
            </div>
            </DndContext>
          )}
        </CardContent>
      </Card>

      {/* Create Investment Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Tambah Investasi Baru</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="manager">Mitra *</Label>
              <Select value={formData.manager_id}
                onValueChange={(value) => setFormData({ ...formData, manager_id: value })} required>
                <SelectTrigger data-testid="add-investment-manager-select"><SelectValue placeholder="Pilih mitra" /></SelectTrigger>
                <SelectContent>
                  {managers.map(manager => (
                    <SelectItem key={manager.id} value={manager.id}>{manager.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-2">Saldo Investasi mitra akan dimulai dari Rp 0.</p>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>Batal</Button>
              <Button type="submit" disabled={submitting} className="bg-teal-600 hover:bg-teal-700" data-testid="submit-investment-btn">{submitting ? 'Menyimpan...' : 'Simpan Investasi'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Transfer Dialog */}
      <Dialog open={transferDialogOpen} onOpenChange={(open) => { setTransferDialogOpen(open); if (!open) resetTransferForm(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Transfer - {transferForm.manager_name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleTransferSubmit} className="space-y-4">
            <div>
              <Label>Tanggal Transfer *</Label>
              <Input
                type="date"
                value={transferForm.transfer_date}
                onChange={(e) => setTransferForm({...transferForm, transfer_date: e.target.value})}
                data-testid="investment-transfer-date-input"
                required
              />
            </div>
            <div>
              <Label>Jenis Transfer *</Label>
              <Select value={transferForm.transfer_type}
                onValueChange={(value) => setTransferForm({...transferForm, transfer_type: value})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="DEPOSIT">
                    <div className="flex items-center gap-2">
                      <ArrowUpCircle size={16} className="text-green-600" />
                      Deposit (Tambah Saldo)
                    </div>
                  </SelectItem>
                  <SelectItem value="WITHDRAW">
                    <div className="flex items-center gap-2">
                      <ArrowDownCircle size={16} className="text-red-600" />
                      Withdraw (Tarik Saldo)
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tipe Rekening *</Label>
              <Select value={transferForm.account_type}
                onValueChange={(value) => setTransferForm({...transferForm, account_type: value, account_id: ''})}>
                <SelectTrigger><SelectValue placeholder="Pilih tipe rekening..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="CENTRAL">Pusat</SelectItem>
                  <SelectItem value="BRANCH">Cabang</SelectItem>
                  <SelectItem value="HUTANG_USAHA">Hutang Usaha</SelectItem>
                  <SelectItem value="CUSTOMER">Customer</SelectItem>
                  <SelectItem value="MANAGER">Laba Rugi Mitra</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {transferForm.account_type && (
              <div>
                <Label>Rekening *</Label>
                <SearchableSelect
                  value={transferForm.account_id}
                  onValueChange={(value) => setTransferForm({...transferForm, account_id: value})}
                  options={getAccountsByType(transferForm.account_type).map(acc => {
                    let label = acc.name || acc.account_name || 'Unnamed Account';
                    if (acc.manager_name) label = `${acc.manager_name} - ${label}`;
                    return { value: acc.id, label: `${label} - ${formatCurrency(acc.balance || 0)}` };
                  })}
                  placeholder="Pilih rekening..."
                  searchPlaceholder="Cari rekening..."
                  emptyText="Rekening tidak ditemukan"
                />
              </div>
            )}
            <div>
              <Label>Jumlah (Rp) *</Label>
              <Input type="text"
                value={transferForm.amount ? parseFloat(transferForm.amount).toLocaleString('id-ID') : ''}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^\d]/g, '');
                  setTransferForm({...transferForm, amount: value});
                }}
                required />
            </div>
            <div>
              <Label>Keterangan *</Label>
              <Input type="text" value={transferForm.description}
                onChange={(e) => setTransferForm({...transferForm, description: e.target.value})}
                placeholder="Masukkan keterangan transaksi..." required />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setTransferDialogOpen(false); resetTransferForm(); }}>Batal</Button>
              <Button type="submit" disabled={submitting} className="bg-teal-600 hover:bg-teal-700" data-testid="investment-transfer-submit-btn">
                {submitting ? 'Memproses...' : (transferForm.transfer_type === 'DEPOSIT' ? 'Deposit' : 'Withdraw')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, investmentId: null, investmentAmount: 0, managerName: '', branchName: '', password: '' })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Konfirmasi Hapus Investasi</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleConfirmDelete}>
            <div className="space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  <strong>Peringatan:</strong> Anda akan menghapus investasi berikut:
                </p>
                <ul className="mt-2 text-sm text-yellow-900 space-y-1">
                  <li>Mitra: <strong>{deleteDialog.managerName}</strong></li>
                  <li>Cabang: <strong>{deleteDialog.branchName}</strong></li>
                  <li>Total Dana Investasi Mitra: <strong>Rp.&nbsp;{(deleteDialog.investmentAmount || 0).toLocaleString('id-ID')}</strong></li>
                </ul>
              </div>
              <div>
                <Label>Password Anda *</Label>
                <Input type="password" value={deleteDialog.password}
                  onChange={(e) => setDeleteDialog({...deleteDialog, password: e.target.value})}
                  placeholder="Masukkan password untuk konfirmasi" required />
              </div>
            </div>
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setDeleteDialog({ open: false, investmentId: null, investmentAmount: 0, managerName: '', branchName: '', password: '' })}>Batal</Button>
              <Button type="submit" variant="destructive" disabled={submitting} data-testid="delete-investment-submit-btn">{submitting ? 'Menghapus...' : 'Hapus Investasi'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Investment History Dialog */}
      <Dialog open={historyDialog.open} onOpenChange={(open) => !open && setHistoryDialog({ open: false, loading: false, data: null })}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="history-dialog">
          <DialogHeader>
            <DialogTitle>
              Riwayat Investasi — {historyDialog.data?.manager_name || historyDialog.managerName || ''}
            </DialogTitle>
          </DialogHeader>

          {historyDialog.loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-500"></div>
            </div>
          ) : historyDialog.data ? (
            <div className="space-y-5">
              {/* Summary */}
              <div className="grid grid-cols-2 gap-3">
                <Card className="bg-teal-50 border-teal-200">
                  <CardContent className="pt-4 pb-4">
                    <p className="text-xs text-teal-700">Total Dana Investasi Saat Ini</p>
                    <p className="text-lg font-bold text-teal-800" data-testid="history-total-investment">
                      {formatCurrency(historyDialog.data.total_investment_amount || 0)}
                    </p>
                  </CardContent>
                </Card>
                <Card className="bg-purple-50 border-purple-200">
                  <CardContent className="pt-4 pb-4">
                    <p className="text-xs text-purple-700">Akumulasi Hasil Pembagian</p>
                    <p className="text-lg font-bold text-purple-800" data-testid="history-total-dividend">
                      {formatCurrency(historyDialog.data.total_dividend || 0)}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Unified: Penambahan Investasi (Deposit) & Penarikan Dana */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-2">Riwayat Investasi</h3>

                {/* View mode: Last 10 (default) or Tanggal (date range) */}
                <div className="mb-3">
                  <RadioGroup
                    value={historyViewMode}
                    onValueChange={(val) => {
                      setHistoryViewMode(val);
                      if (val === 'last10') { setHistoryStartDate(''); setHistoryEndDate(''); }
                    }}
                    className="flex flex-row gap-6"
                    data-testid="history-view-mode-radio"
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="last10" id="history-mode-last10" data-testid="history-mode-last10" />
                      <Label htmlFor="history-mode-last10" className="text-xs cursor-pointer">10 Terakhir</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="date" id="history-mode-date" data-testid="history-mode-date" />
                      <Label htmlFor="history-mode-date" className="text-xs cursor-pointer">Tanggal</Label>
                    </div>
                  </RadioGroup>
                </div>

                {historyViewMode === 'date' && (
                  <div className="grid grid-cols-2 gap-3 mb-3" data-testid="history-date-filter">
                    <div>
                      <Label className="text-xs">Dari Tanggal</Label>
                      <Input type="date" className="h-8 text-xs" value={historyStartDate}
                        onChange={(e) => setHistoryStartDate(e.target.value)} data-testid="history-start-date" />
                    </div>
                    <div>
                      <Label className="text-xs">Sampai Tanggal</Label>
                      <Input type="date" className="h-8 text-xs" value={historyEndDate}
                        onChange={(e) => setHistoryEndDate(e.target.value)} data-testid="history-end-date" />
                    </div>
                  </div>
                )}

                {(() => {
                  const events = (historyDialog.data.investment_events || []).map((ev) => ({
                    id: `inv-${ev.investment_date}-${ev.amount}`,
                    date: ev.investment_date,
                    type: 'DEPOSIT',
                    description: `Penambahan investasi${ev.period_name ? ' (' + ev.period_name + ')' : ''}${(ev.branches && ev.branches.length) ? ' — ' + ev.branches.join(', ') : ''}`,
                    amount: ev.amount || 0
                  })).filter((e) => e.amount > 0);
                  const transfers = (historyDialog.data.transfers || []).map((tx) => {
                    // Liability accounts (Hutang Usaha / Laba Rugi Mitra) memakai arah terbalik
                    const isLiability = ['HUTANG_USAHA', 'MANAGER'].includes(tx.account_type);
                    const fallback = isLiability
                      ? (tx.transaction_type === 'DEBIT' ? 'WITHDRAW' : 'DEPOSIT')
                      : (tx.transaction_type === 'DEBIT' ? 'DEPOSIT' : 'WITHDRAW');
                    return {
                      id: tx.id,
                      kind: 'transfer',
                      refId: tx.id,
                      date: tx.transaction_date || tx.created_at,
                      type: tx.transfer_direction || fallback,
                      description: tx.description || '-',
                      amount: Math.abs(tx.amount || 0)
                    };
                  });
                  const profits = (historyDialog.data.dividends || []).map((d) => ({
                    id: `profit-${d.id}`,
                    kind: 'profit',
                    refId: d.id,
                    date: d.expense_date,
                    type: 'PROFIT',
                    description: d.description || 'Hasil Invest',
                    amount: d.amount || 0
                  }));
                  const combined = [...events, ...transfers, ...profits].sort((a, b) => String(a.date || '').localeCompare(String(b.date || '')));

                  if (combined.length === 0) {
                    return <p className="text-xs text-slate-500 italic" data-testid="history-empty">Belum ada History</p>;
                  }

                  let displayed = combined;
                  if (historyViewMode === 'last10') {
                    displayed = combined.slice(-10);
                  } else {
                    displayed = combined.filter((r) => {
                      if (!r.date) return false;
                      const d = String(r.date).slice(0, 10);
                      if (historyStartDate && d < historyStartDate) return false;
                      if (historyEndDate && d > historyEndDate) return false;
                      return true;
                    });
                  }

                  const saldoAkhir = historyDialog.data.total_investment_amount || 0;

                  return (
                    <div className="border rounded-md overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-xs">Tanggal</TableHead>
                            <TableHead className="text-xs">Tipe</TableHead>
                            <TableHead className="text-xs">Keterangan</TableHead>
                            <TableHead className="text-xs text-right">Jumlah</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {displayed.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={4} className="text-xs text-center text-slate-500 italic py-4">
                                Tidak ada transaksi pada rentang tanggal ini
                              </TableCell>
                            </TableRow>
                          ) : displayed.map((row) => (
                            <TableRow key={row.id}>
                              <TableCell className="text-xs">
                                {row.date ? new Date(row.date).toLocaleDateString('id-ID') : '-'}
                              </TableCell>
                              <TableCell className="text-xs">
                                <span className={`text-[10px] px-1.5 py-0.5 rounded ${row.type === 'WITHDRAW' ? 'bg-red-100 text-red-700' : row.type === 'PROFIT' ? 'bg-purple-100 text-purple-700' : 'bg-green-100 text-green-700'}`}>
                                  {row.type === 'PROFIT' ? 'Profit' : row.type}
                                </span>
                              </TableCell>
                              <TableCell className="text-xs">
                                {editingDesc.id === row.id ? (
                                  <Input
                                    autoFocus
                                    className="h-7 text-xs"
                                    value={editingDesc.value}
                                    disabled={editingDesc.saving}
                                    data-testid={`history-desc-input-${row.id}`}
                                    onChange={(e) => setEditingDesc((p) => ({ ...p, value: e.target.value }))}
                                    onBlur={() => saveHistoryDescription(row)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') { e.preventDefault(); saveHistoryDescription(row); }
                                      if (e.key === 'Escape') setEditingDesc({ id: null, value: '', saving: false });
                                    }}
                                  />
                                ) : row.refId ? (
                                  <button
                                    type="button"
                                    className="text-left w-full rounded px-1 -mx-1 hover:bg-teal-50 hover:ring-1 hover:ring-teal-300 transition-colors cursor-text"
                                    title="Klik untuk edit keterangan"
                                    data-testid={`history-desc-cell-${row.id}`}
                                    onClick={() => setEditingDesc({ id: row.id, value: row.description === '-' ? '' : row.description, saving: false })}
                                  >
                                    {row.description}
                                  </button>
                                ) : (
                                  row.description
                                )}
                              </TableCell>
                              <TableCell className={`text-xs text-right font-medium ${row.type === 'WITHDRAW' ? 'text-red-700' : row.type === 'PROFIT' ? 'text-purple-700' : 'text-green-700'}`}>
                                {row.type === 'WITHDRAW' ? '-' : '+'}{formatCurrency(row.amount)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                        <TableFooter>
                          <TableRow className="bg-slate-50 border-t-2 border-slate-300">
                            <TableCell colSpan={3} className="text-xs font-bold text-slate-800 text-right">
                              Saldo Akhir
                            </TableCell>
                            <TableCell
                              className={`text-xs text-right font-bold ${saldoAkhir >= 0 ? 'text-green-700' : 'text-red-700'}`}
                              data-testid="history-saldo-akhir"
                            >
                              {formatCurrency(saldoAkhir)}
                            </TableCell>
                          </TableRow>
                        </TableFooter>
                      </Table>
                    </div>
                  );
                })()}
              </div>
            </div>
          ) : null}

          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryDialog({ open: false, loading: false, data: null })}>
              Tutup
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pembagian Investasi Dialog */}
      <Dialog open={pembagianDialog} onOpenChange={setPembagianDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="pembagian-dialog">
          <DialogHeader>
            <DialogTitle>Pembagian Investasi</DialogTitle>
          </DialogHeader>
          <div className="flex items-center gap-2 mb-2 bg-slate-50 border rounded-md px-3 py-2">
            <Label className="text-xs font-medium text-slate-600 whitespace-nowrap">Set semua % Invest</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={bulkPembagianPct}
              placeholder="mis. 1.5"
              onChange={(e) => {
                const val = e.target.value;
                setBulkPembagianPct(val);
                setPembagianRows(prev => {
                  const next = {};
                  Object.keys(prev).forEach(mid => {
                    next[mid] = (prev[mid] || []).map(() => ({ pct: val, hasil: '' }));
                  });
                  return next;
                });
              }}
              className="w-28 h-8 text-right"
              data-testid="bulk-pct-input"
            />
            <span className="text-xs">%</span>
            <span className="text-xs text-slate-500 ml-2">(masih bisa diedit per baris)</span>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Mitra</TableHead>
                  <TableHead className="text-right">Saldo Investasi</TableHead>
                  <TableHead>Bulan</TableHead>
                  <TableHead className="text-right">% Invest</TableHead>
                  <TableHead className="text-right">Hasil Invest</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {managerInvestments.map((m) => {
                  const rows = pembagianRows[m.manager_id] || [];
                  const base = (m.investment_amount || 0) - (distributedMap[m.manager_id] || 0);
                  return rows.map((row, monthIdx) => {
                    const pctActive = row && row.pct !== '' && row.pct !== null && row.pct !== undefined;
                    const hasil = rowHasil(row, base);
                    return (
                      <TableRow key={`${m.manager_id}-${monthIdx}`} data-testid={`pembagian-row-${m.manager_id}-${monthIdx}`}>
                        {monthIdx === 0 && (
                          <TableCell rowSpan={rows.length} className="font-medium align-top">{m.manager_name}</TableCell>
                        )}
                        {monthIdx === 0 && (
                          <TableCell rowSpan={rows.length} className="text-right align-top text-teal-700 font-medium">{formatCurrency(base)}</TableCell>
                        )}
                        <TableCell className="text-slate-600" data-testid={`pembagian-bulan-${m.manager_id}-${monthIdx}`}>
                          {currentPeriodMonthsList[monthIdx] ? monthLabel(currentPeriodMonthsList[monthIdx]) : `Bulan ${monthIdx + 1}`}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={row ? row.pct : ''}
                              placeholder="-"
                              onChange={(e) => {
                                const val = e.target.value;
                                setPembagianRows(prev => {
                                  const next = { ...prev };
                                  const arr = [...(next[m.manager_id] || [])];
                                  arr[monthIdx] = { ...(arr[monthIdx] || { pct: '', hasil: '' }), pct: val };
                                  next[m.manager_id] = arr;
                                  return next;
                                });
                              }}
                              className="w-24 text-right h-8"
                              data-testid={`pembagian-pct-${m.manager_id}-${monthIdx}`}
                            />
                            <span className="text-xs">%</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          {pctActive ? (
                            <span className="font-semibold text-purple-700 pr-1" data-testid={`pembagian-hasil-${m.manager_id}-${monthIdx}`}>
                              {formatCurrency(hasil)}
                            </span>
                          ) : (
                            <div className="flex items-center justify-end gap-1">
                              <span className="text-xs">Rp</span>
                              <Input
                                type="text"
                                inputMode="numeric"
                                value={row && row.hasil ? Number(row.hasil).toLocaleString('id-ID') : ''}
                                placeholder="0"
                                onChange={(e) => {
                                  const digits = e.target.value.replace(/[^\d]/g, '');
                                  setPembagianRows(prev => {
                                    const next = { ...prev };
                                    const arr = [...(next[m.manager_id] || [])];
                                    arr[monthIdx] = { ...(arr[monthIdx] || { pct: '', hasil: '' }), hasil: digits };
                                    next[m.manager_id] = arr;
                                    return next;
                                  });
                                }}
                                className="w-32 text-right h-8"
                                data-testid={`pembagian-hasil-input-${m.manager_id}-${monthIdx}`}
                              />
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  });
                })}
              </TableBody>
              <TableFooter>
                <TableRow className="bg-purple-50 border-t-2 border-purple-600">
                  <TableCell colSpan={4} className="text-right font-bold text-purple-900">TOTAL HASIL INVEST</TableCell>
                  <TableCell className="text-right font-bold text-purple-900 text-lg" data-testid="pembagian-total-hasil">
                    {formatCurrency(pembagianTotalHasil)}
                  </TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setPembagianDialog(false)}>Batal</Button>
            <Button type="button" className="bg-purple-600 hover:bg-purple-700" onClick={submitPembagian} disabled={submittingPembagian} data-testid="submit-pembagian-btn">
              {submittingPembagian ? 'Memproses...' : 'Proses Pembagian'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Investments;
